import { z } from "zod";
import { useFunnel } from "@airhelp/funnel";
import { useTranslation } from "react-i18next";
import { ControlledRadioGroup, useForm, useFormHandlers } from "../../../modules/form";
import { FunnelStepContainer } from "../../../components/funnel/ui-elements/FunnelStepContainer";
import { FunnelStepSection } from "../../../components/funnel/ui-elements/FunnelStepSection";
import {
  FunnelStepBackControl,
  FunnelStepControls,
  FunnelStepForwardControl,
} from "../../../components/funnel/ui-elements/FunnelStepControls";
import {
  getArrivalAirport,
  updateClaimDetails,
  usePreEligibilityDataSnapshot,
} from "../state/preEligibilityState";
import { SUPPORTED_DELAYS } from "../../../settings/constants";
import { useCreateClaimEnquiry } from "../../../modules/hooks/useCreateClaimEnquiry";
import { useDelayOptions } from "../../../modules/hooks/useDelayOptions";
import { ErrorNotification } from "v2/components/funnel/ui-elements/ErrorNotification";
import { trackClaimProcess } from "../../../modules/analytics/track";
import { useTrackEnquiryStepComplete } from "../../../modules/analytics/hooks/useTrackEnquiryStepComplete";
import { ControlledSelect } from "../../../modules/form/ControlledSelect";
import { getSelectOptions } from "./DelayInformation";
import { ControlledCheckBox } from "v2/modules/form/ControlledCheckbox";

const stepSchema = z
  .object({
    totalDelay: z.enum(SUPPORTED_DELAYS).nullable(),
    neverArrived: z.boolean().nullable(),
  })
  .refine(({ totalDelay, neverArrived }) => {
    if (!!totalDelay || neverArrived) {
      return true;
    }
    return false;
  });

const defaultStepSchema = z.object({
  totalDelay: z.enum(SUPPORTED_DELAYS).nullable(),
  neverArrived: z.boolean().nullable(),
});

export function TotalDelayStep() {
  const funnel = useFunnel();
  const { t } = useTranslation();
  const { claimDetails, itineraryFlights } = usePreEligibilityDataSnapshot();
  const { options, optionsWithTranslations } = useDelayOptions({
    potentialRegulation: claimDetails.potentialRegulation,
    claimType: claimDetails.claimType,
  });

  const arrivalAirport = getArrivalAirport();

  const { createClaimEnquiry } = useCreateClaimEnquiry();

  const trackEnquiryStepComplete = useTrackEnquiryStepComplete({
    currentStep: funnel.currentStep?.name,
  });
  const isSaudi = claimDetails.potentialRegulation === "saudi_arabia";

  const {
    control,
    watch,
    setValue,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm({
    schema: stepSchema,
    defaultValues: {
      totalDelay: claimDetails.totalDelay,
      neverArrived: claimDetails.totalDelay === "never",
    },
  });

  const neverArrived = watch("neverArrived");

  const { onSubmit, showError } = useFormHandlers({
    schema: defaultStepSchema,
    async onSubmit({ totalDelay }) {
      const updatedClaimDetails = updateClaimDetails({
        totalDelay: neverArrived ? "never" : totalDelay,
      });

      if (funnel.currentStep?.meta?.claimShouldBeSubmitted) {
        const rawClaimEnquiry = await createClaimEnquiry({
          itineraryFlights,
          claimDetails: updatedClaimDetails,
        });
        trackClaimProcess({
          funnelStep: funnel.currentStep?.name,
          isEligible: rawClaimEnquiry?.claim_valid,
        });
        trackEnquiryStepComplete();
      } else {
        trackClaimProcess({ funnelStep: funnel.currentStep?.name });
        trackEnquiryStepComplete();
        funnel.stepForward();
      }
    },
  });

  return (
    <FunnelStepContainer data-testid={"TotalDelayStep"}>
      <FunnelStepSection
        title={t("steps.total_delay.text", {
          city: arrivalAirport?.city,
          iata: arrivalAirport?.iata,
        })}
      >
        {isSaudi ? (
          <>
            <ControlledSelect
              isDisabled={neverArrived}
              dataTestId="totalDelay"
              name="totalDelay"
              control={control}
              options={getSelectOptions(options, optionsWithTranslations, t).filter(
                (option) => option.value !== "never",
              )}
            />
            <ControlledCheckBox
              name="neverArrived"
              data-testId="neverArrived"
              control={control}
              mt="6"
              handleChange={() => setValue("totalDelay", null)}
            >
              {t("steps.total_delay.saudi_arabia.never_arrived")}
            </ControlledCheckBox>
          </>
        ) : (
          <ControlledRadioGroup
            name="totalDelay"
            control={control}
            options={options}
            // @ts-ignore
            renderOption={(option) => t(optionsWithTranslations[option])}
          />
        )}
      </FunnelStepSection>

      {showError && <ErrorNotification message={t("components.funnel_step.try_again")} />}

      <FunnelStepControls>
        <FunnelStepBackControl />
        <FunnelStepForwardControl
          isDisabled={!isValid}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        />
      </FunnelStepControls>
    </FunnelStepContainer>
  );
}
